import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "@/lib/api";
import { onLogout } from "@/stores/authStore";
import { setSentryGroup } from "@/lib/sentry";
import type {
  Group,
  CreateGroupRequest,
  UpdateGroupMetadataPayload,
  UpdateGroupPreferencesPayload,
} from '@/types';

interface GroupState {
  groups: Group[];
  activeGroup: Group | null;
  isLoading: boolean;
  subscriptionRevision: number;
  fetchGroups: () => Promise<void>;
  fetchActiveGroup: (groupUuid?: string) => Promise<void>;
  setActiveGroup: (group: Group) => void;
  clearGroups: () => void;
  createGroup: (data: CreateGroupRequest) => Promise<Group>;
  updateGroup: (groupUuid: string, data: UpdateGroupMetadataPayload) => Promise<Group>;
  joinGroup: (groupUuid: string, token: string) => Promise<Group>;
  joinViaLink: (token: string) => Promise<Group>;
  joinViaCode: (code: string) => Promise<Group>;
  bumpSubscriptionRevision: () => void;
  updateGroupMetadata: (groupUuid: string, data: UpdateGroupMetadataPayload) => Promise<Group>;
  updateGroupPreferences: (groupUuid: string, data: UpdateGroupPreferencesPayload) => Promise<Group>;
  uploadGroupLogo: (groupUuid: string, file: File) => Promise<Group>;
  deleteGroupLogo: (groupUuid: string) => Promise<Group>;
  uploadRegistrationCertificate: (groupUuid: string, file: File) => Promise<Group>;
  deleteRegistrationCertificate: (groupUuid: string) => Promise<Group>;
}

export const useGroupStore = create<GroupState>()(
  persist(
    (set, get) => ({
      groups: [],
      activeGroup: null,
      isLoading: false,
      subscriptionRevision: 0,

      fetchGroups: async () => {
        set({ isLoading: true });
        try {
          const { data } = await api.get<Group[]>("/groups");
          const currentActive = get().activeGroup;
          // Match active group by uuid (id is not exposed by the API)
          const freshActive = currentActive
            ? data.find((g) => g.uuid === currentActive.uuid) ?? null
            : null;
          const nextActiveGroup = freshActive ?? data[0] ?? null;

          set({
            groups: data,
            activeGroup: nextActiveGroup,
            isLoading: false,
          });

          if (nextActiveGroup) {
            await get().fetchActiveGroup(nextActiveGroup.uuid);
          }
        } catch {
          set({ isLoading: false });
        }
      },

      fetchActiveGroup: async (groupUuid) => {
        const targetGroupUuid = groupUuid ?? get().activeGroup?.uuid;
        if (!targetGroupUuid) return;

        const { data: group } = await api.get<Group>(`/groups/${targetGroupUuid}`);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === targetGroupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === targetGroupUuid ? { ...g, ...group } : g)),
        }));
      },

      setActiveGroup: (group: Group) => {
        set({ activeGroup: group });
        setSentryGroup(group);
        void get().fetchActiveGroup(group.uuid);
      },

      clearGroups: () => set({ groups: [], activeGroup: null, isLoading: false }),

      bumpSubscriptionRevision: () =>
        set((state) => ({ subscriptionRevision: state.subscriptionRevision + 1 })),

      createGroup: async (data: CreateGroupRequest) => {
        set({ isLoading: true });
        try {
          const { data: group } = await api.post<Group>("/groups", data);
          set((state) => ({
            groups: [...state.groups, group],
            activeGroup: group,
            isLoading: false,
          }));
          return group;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateGroup: async (groupUuid: string, data: UpdateGroupMetadataPayload) => {
        set({ isLoading: true });
        try {
          const { data: group } = await api.put<Group>(`/groups/${groupUuid}`, data);
          set((state) => ({
            groups: state.groups.map((existingGroup) =>
              existingGroup.uuid === group.uuid ? group : existingGroup,
            ),
            activeGroup: state.activeGroup?.uuid === group.uuid ? group : state.activeGroup,
            isLoading: false,
          }));
          if (get().activeGroup?.uuid === group.uuid) {
            setSentryGroup(group);
          }
          return group;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      joinGroup: async (groupUuid: string, token: string) => {
        set({ isLoading: true });
        try {
          const { data: group } = await api.post<Group>(
            `/groups/${groupUuid}/accept-invitation`,
            { token },
          );
          set((state) => ({
            groups: [...state.groups, group],
            activeGroup: group,
            isLoading: false,
          }));
          return group;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      joinViaLink: async (token: string) => {
        set({ isLoading: true });
        try {
          const { data: group } = await api.post<Group>(`/invite-links/${token}/join`);
          set((state) => ({
            groups: [...state.groups, group],
            activeGroup: group,
            isLoading: false,
          }));
          return group;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      joinViaCode: async (code: string) => {
        set({ isLoading: true });
        try {
          const { data: group } = await api.post<Group>('/invite-links/join-by-code', { code });
          set((state) => ({
            groups: [...state.groups, group],
            activeGroup: group,
            isLoading: false,
          }));
          return group;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      updateGroupMetadata: async (groupUuid, data) => {
        const { data: group } = await api.put<Group>(`/groups/${groupUuid}`, data);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },

      updateGroupPreferences: async (groupUuid, data) => {
        const { data: group } = await api.put<Group>(`/groups/${groupUuid}/preferences`, data);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },

      uploadGroupLogo: async (groupUuid, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        const { data: group } = await api.post<Group>(`/groups/${groupUuid}/logo`, formData);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },

      deleteGroupLogo: async (groupUuid) => {
        const { data: group } = await api.delete<Group>(`/groups/${groupUuid}/logo`);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },

      uploadRegistrationCertificate: async (groupUuid, file) => {
        const formData = new FormData();
        formData.append('certificate', file);
        const { data: group } = await api.post<Group>(`/groups/${groupUuid}/registration-certificate`, formData);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },

      deleteRegistrationCertificate: async (groupUuid) => {
        const { data: group } = await api.delete<Group>(`/groups/${groupUuid}/registration-certificate`);
        set((state) => ({
          activeGroup: state.activeGroup?.uuid === groupUuid ? group : state.activeGroup,
          groups: state.groups.map((g) => (g.uuid === groupUuid ? group : g)),
        }));
        return group;
      },
    }),
    {
      name: "group-storage",
      partialize: (state) => ({ activeGroup: state.activeGroup }),
    },
  ),
);

// Register group cleanup to run whenever the user logs out.
// This replaces the direct cross-store import that was previously in authStore.
onLogout(() => useGroupStore.getState().clearGroups());
